import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  ViewChild,
  HostListener,
  DestroyRef,
  afterNextRender,
  effect,
  input,
  signal,
  inject,
  output,
  PLATFORM_ID,
  NgZone,
  OnDestroy,
} from '@angular/core';
import * as THREE from 'three';
import { gsap } from 'gsap';
import GUI from 'lil-gui';
import { isPlatformBrowser } from '@angular/common';
import { paramProposals } from './addyourfavoriteparams';
import { Platform } from '@angular/cdk/platform';
import { GtagService } from '../../services/gtag.service';

@Component({
  selector: 'dot-mask',
  standalone: true,
  templateUrl: './dot-mask.component.html',
  styleUrls: ['./dot-mask.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DotMaskComponent implements OnDestroy {
  platform = inject(Platform);
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  // Component inputs with default values for customization
  background = input<string>();
  spacing = input<number>();
  fill = input<number>();
  mouseRadius = input<number>();
  invert = input<boolean>(true);
  unfoldGui = input<boolean>(false);
  debug = input<boolean>(true);
  lowPerformance = input<boolean>(false);
  maxDpr = input<number>(2);

  private platformId = inject(PLATFORM_ID);
  private ngZone = inject(NgZone);
  private destroyRef = inject(DestroyRef);
  private gtagService = inject(GtagService);

  // Mouse movement tracking for analytics
  private totalMouseDistance = 0;
  private lastMousePosition = { x: -9999, y: -9999 };
  private hasTrackedMouseMovement = false;

  // Timeline for reveal animations
  public revealTl?: gsap.core.Timeline;
  private revealDefaults?: {
    mouseRadius: number;
    fillRevealTarget: number;
    fillWaveWidth: number;
    fillWaveR: number;
    fill: number;
    mouseNoiseAmp: number;
  };

  // Three.js rendering components
  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.Camera;
  private mesh!: THREE.Mesh;
  private uniforms!: Record<string, THREE.IUniform>;
  private raf = 0;

  // Viewport and performance tracking
  private size = signal({ w: 0, h: 0, dpr: 1 });
  private isInViewport = signal(true);
  private intersectionObserver?: IntersectionObserver;

  // Mouse interaction state
  private targetMouse = signal({ x: -9999, y: -9999 });
  private smoothMouse = signal({ x: -9999, y: -9999 });
  private smoothTween?: gsap.core.Tween;

  // Gyroscope/device orientation state
  private gyroActive = false;
  private neutral = { beta: 0, gamma: 0 };
  private lastGyro = { x: -9999, y: -9999, t: performance.now() };
  private lastRawTilt = { beta: 0, gamma: 0 };
  private hasNeutral = false;

  // Velocity calculation for mouse boost effects
  private prevTarget = { x: -9999, y: -9999, t: performance.now() };

  // GUI and debugging parameters
  private params = paramProposals[0].params;
  private gui?: GUI;
  private fpsEl?: HTMLDivElement;
  private lastTime = performance.now();
  private frames = 0;
  private fps = 0;

  // Performance optimization: track which params changed
  private lastParams: any = {};
  private lastRenderTime = 0;
  private readonly RENDER_THROTTLE_MS = 16; // ~60fps (can be increased to 33 for 30fps)

  // Cache frequently accessed values to avoid recalculation
  private cachedDevicePixelRatio = 1;
  private cachedSizeData = { w: 0, h: 0, dpr: 1 };

  // Event listeners for cleanup
  private eventListeners: Array<{ element: any; event: string; handler: any }> = [];

  // Component lifecycle events
  inited = output<boolean>();
  emittedInited = signal(false);

  disableOnLowPerformance = effect(() => {
    if (this.lowPerformance()) {
      // make sure to reset state
      this.resetReveal();
      this.eventListeners.forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler);
      });
      this.eventListeners = [];

      this.setDefaultMouseIfUninitialized();
      this.renderSingleFrame(performance.now());
      this.cleanup();
    }
  });

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) return;

      this.setupIntersectionObserver();
      this.init();

      this.enableGyroIfTouch();

      this.setDefaultMouseIfUninitialized();

      const canvas = this.canvasRef.nativeElement;

      // Add pointer move listener for mouse interaction
      const pointerMoveHandler = (e: Event) => {
        const pointerEvent = e as PointerEvent;

        if (this.gyroActive) return; // Gyroscope controls mouse on touch devices
        if (this.isTouchDevice() || pointerEvent.pointerType === 'touch') return; // Ignore touch events on non-touch devices

        const rect = canvas.getBoundingClientRect();
        const normalizedX = pointerEvent.clientX - rect.left;
        const normalizedY = pointerEvent.clientY - rect.top;
        const now = performance.now();

        // Track mouse movement distance for analytics
        if (this.lastMousePosition.x !== -9999 && this.lastMousePosition.y !== -9999) {
          const distanceMoved = Math.sqrt(
            Math.pow(normalizedX - this.lastMousePosition.x, 2) + Math.pow(normalizedY - this.lastMousePosition.y, 2)
          );
          this.totalMouseDistance += distanceMoved;
        }
        this.lastMousePosition = { x: normalizedX, y: normalizedY };

        // Update target mouse position
        this.targetMouse.set({ x: normalizedX, y: normalizedY });

        // Firefox: Also update smooth mouse immediately for better responsiveness
        const isFirefox = this.platform.FIREFOX;
        if (isFirefox) {
          this.smoothMouse.set({ x: normalizedX, y: normalizedY });
        }

        // Create smooth mouse following animation using GSAP
        this.smoothTween?.kill();
        const smoothMousePosition = { ...this.smoothMouse() };
        this.smoothTween = gsap.to(smoothMousePosition, {
          x: normalizedX,
          y: normalizedY,
          duration: this.params.followDuration,
          ease: this.params.followEase,
          onUpdate: () => {
            this.smoothMouse.set({ x: smoothMousePosition.x, y: smoothMousePosition.y });
          },
        });

        // Calculate velocity for radius boost effect
        const deltaTime = Math.max(1, now - this.prevTarget.t);
        const deltaX = normalizedX - this.prevTarget.x;
        const deltaY = normalizedY - this.prevTarget.y;
        const velocityPixelsPerSecond = (Math.hypot(deltaX, deltaY) * 1000) / deltaTime;
        this.prevTarget = { x: normalizedX, y: normalizedY, t: now };

        // Apply velocity-based radius boost with capping
        const boostTarget = Math.min(velocityPixelsPerSecond * this.params.velocityScale, this.params.velocityMaxBoost);
        gsap.to(this.params, {
          radiusBoost: boostTarget,
          duration: this.params.boostResponse,
          ease: 'power2.out',
        });
      };

      // Add pointer leave listener for cleanup animation
      const pointerLeaveHandler = () => {
        // Reset velocity boost when pointer leaves the canvas
        gsap.to(this.params, {
          radiusBoost: 0,
          duration: this.params.boostResponse,
          ease: 'power2.out',
        });
      };

      this.addEventListener(canvas, 'pointermove', pointerMoveHandler);
      this.addEventListener(canvas, 'pointerleave', pointerLeaveHandler);
    });

    // Effect to handle size changes and update renderer
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      const sizeData = this.size();
      if (!this.uniforms || !this.renderer) return;

      // Cache size data to avoid signal reads in render loop
      this.cachedSizeData = { ...sizeData };

      // Safari/Firefox-specific: limit pixel ratio to reduce GPU memory usage
      const devicePixelRatio = this.renderDpr(sizeData.dpr);

      // Cache pixel ratio to avoid recalculation every frame
      this.cachedDevicePixelRatio = devicePixelRatio;

      this.renderer.setPixelRatio(devicePixelRatio);
      this.renderer.setSize(sizeData.w, sizeData.h, false);
      this.uniforms['u_res'].value.set(sizeData.w * devicePixelRatio, sizeData.h * devicePixelRatio);
    });

    // Effect to handle parameter updates and sync with uniforms
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      if (!this.uniforms) return;

      // Set initial parameters from inputs if GUI hasn't been created yet
      if (!this.gui) {
        this.params.background = this.background() || 'rgb(1,65,57)';
        this.params.spacing = this.spacing() || 10;
        this.params.fill = this.fill() || 1;
        this.params.mouseRadius = this.mouseRadius() || 100;
        this.params.invert = this.invert() || false;
      }

      // Update shader uniforms with current parameter values
      (this.uniforms['u_bg'].value as THREE.Color).set(this.params.background);
      this.uniforms['u_spacing'].value = this.params.spacing;
      this.uniforms['u_radius'].value = this.params.spacing * this.params.fill;
      this.uniforms['u_invert'].value = this.params.invert ? 1.0 : 0.0;
    });
  }

  private sendMouseTrackingEvent(): void {
    // Prevent duplicate tracking
    if (this.hasTrackedMouseMovement) return;

    // Only track if there was mouse movement
    if (this.totalMouseDistance === 0) return;

    this.hasTrackedMouseMovement = true;

    // Calculate mouse distance as percentage of screen diagonal
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const screenDiagonal = Math.sqrt(screenWidth * screenWidth + screenHeight * screenHeight);
    const mouseDistancePercentage = (this.totalMouseDistance / screenDiagonal) * 100;

    let deviceType = 'desktop';
    if (screenWidth < 768) {
      deviceType = 'mobile';
    } else if (screenWidth >= 768 && screenWidth < 1024) {
      deviceType = 'tablet';
    }

    this.gtagService.sendEvent('dot_mask_interaction', {
      event_category: 'engagement',
      event_label: 'mouse-movement',
      value: Math.round(mouseDistancePercentage),
      mouse_distance_percent: Math.round(mouseDistancePercentage * 10) / 10, // One decimal place
      mouse_move_distance_category: mouseDistancePercentage < 200 ? 'low' : mouseDistancePercentage < 1000 ? 'medium' : 'high',
      device_type: deviceType,
    });
  }

  ngOnDestroy(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Send mouse tracking event before cleanup
    this.sendMouseTrackingEvent();

    this.cleanup();

    // reset state
    this.resetReveal();
    this.revealTl = undefined;
    this.smoothTween = undefined;
  }

  /**
   * Helper method to add event listeners and track them for cleanup
   */
  private addEventListener(element: HTMLElement | Window, event: string, handler: EventListener): void {
    element.addEventListener(event, handler);
    this.eventListeners.push({ element, event, handler });
  }

  /**
   * Setup intersection observer to pause rendering when component is out of viewport
   */
  private setupIntersectionObserver(): void {
    if (!('IntersectionObserver' in window)) return;

    this.intersectionObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          this.isInViewport.set(entry.isIntersecting);
        });
      },
      { threshold: 0.1 } // Trigger when 10% of component is visible
    );

    this.intersectionObserver.observe(this.canvasRef.nativeElement);
  }

  /**
   * Comprehensive cleanup method for all resources
   */
  private cleanup(): void {
    // Stop animation loop
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    // Clean up GSAP animations
    this.smoothTween?.kill();
    this.revealTl?.kill();

    // Remove all tracked event listeners
    this.eventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.eventListeners = [];

    // Remove device orientation listener
    window.removeEventListener('deviceorientation', this.onDeviceOrientation);

    // Clean up intersection observer
    this.intersectionObserver?.disconnect();

    // Destroy GUI
    this.gui?.destroy();

    // Remove FPS display element
    if (this.fpsEl && this.fpsEl.parentElement) {
      this.fpsEl.parentElement.removeChild(this.fpsEl);
    }

    // Dispose of Three.js resources
    if (this.renderer) {
      this.renderer.dispose();
    }

    if (this.mesh?.geometry) {
      this.mesh.geometry.dispose();
    }

    if (this.mesh?.material) {
      if (Array.isArray(this.mesh.material)) {
        this.mesh.material.forEach(material => material.dispose());
      } else {
        this.mesh.material.dispose();
      }
    }
  }

  /** True on phones/tablets, where the fill-rate of a full-screen canvas is the bottleneck */
  private isMobile(): boolean {
    return this.platform.IOS || this.platform.ANDROID;
  }

  /** Pixel ratio the params (spacing, radii) are authored for: a 2x (Retina) screen */
  private readonly DESIGN_DPR = 2;

  /** Pixel ratio the canvas actually renders at, capped for performance (lower still on mobile) */
  private renderDpr(deviceDpr: number): number {
    const slowBrowser = this.platform.SAFARI || this.platform.FIREFOX;
    const slowCap = this.platform.FIREFOX ? 1 : 1.5;
    const cap = slowBrowser ? Math.min(this.maxDpr(), slowCap) : this.maxDpr();
    const dpr = Math.min(deviceDpr, cap);
    return this.isMobile() ? Math.min(dpr, 1) : dpr;
  }

  /** Shader scale that keeps dot size identical in CSS pixels regardless of the render resolution */
  private dprScale(deviceDpr: number): number {
    return this.renderDpr(deviceDpr) / this.DESIGN_DPR;
  }

  private init() {
    const canvas = this.canvasRef.nativeElement;
    const lowPerformance = this.platform.SAFARI || this.platform.FIREFOX;

    // Initialize Three.js renderer with optimized settings
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: false, // Antialiasing handled in shader for better performance
      premultipliedAlpha: false, // Prevents darkening at edges
      powerPreference: lowPerformance ? 'default' : 'high-performance',
      failIfMajorPerformanceCaveat: false, // Don't fail on slower devices
      preserveDrawingBuffer: false, // Better performance, no buffer preservation needed
      depth: false, // We don't need depth buffer for 2D shader
      stencil: false, // We don't need stencil buffer
    });
    THREE.ColorManagement.enabled = false;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setClearColor(0x000000, 0);

    // Limit pixel ratio to reduce GPU load (Safari/Firefox, and mobile)
    this.renderer.setPixelRatio(this.renderDpr(window.devicePixelRatio));

    // Initialize Three.js scene and camera
    this.scene = new THREE.Scene();
    this.camera = new THREE.Camera();
    this.scene.add(this.camera);

    // Create fullscreen quad geometry for shader rendering
    const geometry = new THREE.PlaneGeometry(2, 2);

    // Shader uniforms for real-time parameter control
    this.uniforms = {
      u_res: { value: new THREE.Vector2(1, 1) },
      u_mouse: { value: new THREE.Vector2(-9999, -9999) },
      u_time: { value: 0 },
      u_dpr: { value: 1.0 }, // Device pixel ratio for consistent dot sizing

      u_spacing: { value: this.params.spacing },
      u_radius: { value: this.params.spacing * this.params.fill },
      u_bg: { value: new THREE.Color(this.params.background).convertSRGBToLinear() },

      u_noiseScale: { value: this.params.noiseScale },
      u_noiseAmp: { value: this.params.noiseAmp },
      u_noiseSpeed: { value: this.params.noiseSpeed },
      u_threshold: { value: this.params.threshold },

      u_mouseRadiusBase: { value: this.params.mouseRadius },
      u_mouseRadiusBoost: { value: this.params.radiusBoost },
      u_mouseFalloff: { value: this.params.mouseFalloff },
      u_mouseAmp: { value: this.params.mouseAmp },

      u_mouseNoiseScale: { value: this.params.mouseNoiseScale },
      u_mouseNoiseAmp: { value: this.params.mouseNoiseAmp },
      u_mouseNoiseSpeed: { value: this.params.mouseNoiseSpeed },

      u_fillFrom: { value: this.params.fill },
      u_fillTo: { value: this.params.fillRevealTarget },
      u_fillWaveR: { value: this.params.fillWaveR },
      u_fillWaveW: { value: this.params.fillWaveWidth },

      u_invert: { value: this.params.invert ? 1.0 : 0.0 },
    };

    const material = new THREE.ShaderMaterial({
      vertexShader: `void main(){ gl_Position = vec4(position,1.0); }`,
      fragmentShader: `
        precision highp float;
        #include <common>

        uniform vec2  u_res;
        uniform vec2  u_mouse;
        uniform float u_time;
        uniform float u_dpr;

        uniform float u_spacing;
        uniform float u_radius;
        uniform vec3  u_bg;

        // global field
        uniform float u_noiseScale;
        uniform float u_noiseAmp;
        uniform float u_noiseSpeed;
        uniform float u_threshold;

        // mouse influence (with velocity boost + noisy boundary)
        uniform float u_mouseRadiusBase;
        uniform float u_mouseRadiusBoost;
        uniform float u_mouseFalloff;
        uniform float u_mouseAmp;

        uniform float u_mouseNoiseScale;
        uniform float u_mouseNoiseAmp;
        uniform float u_mouseNoiseSpeed;

        uniform float u_fillFrom;    // base fill (params.fill)
        uniform float u_fillTo;      // target fill (params.fillRevealTarget)
        uniform float u_fillWaveR;   // wave radius in px
        uniform float u_fillWaveW;   // wave “band” width in px


        uniform float u_invert;

        // --- small value noise ---
        float hash21(vec2 p){
          p = fract(p*vec2(123.34, 345.45));
          p += dot(p, p+34.345);
          return fract(p.x*p.y);
        }
        float valueNoise(vec2 p){
          vec2 i = floor(p);
          vec2 f = fract(p);
          float a = hash21(i);
          float b = hash21(i+vec2(1.0,0.0));
          float c = hash21(i+vec2(0.0,1.0));
          float d = hash21(i+vec2(1.0,1.0));
          vec2 u = f*f*(3.0-2.0*f);
          return mix(mix(a,b,u.x), mix(c,d,u.x), u.y);
        }

        float insideCircle(vec2 p, float r){ return step(length(p), r); }

         // Analytic AA circle: returns 1 inside, 0 outside, with a 1px smooth edge
        float circleAA(vec2 p, float r) {
          float dist = length(p) - r;
          float w = fwidth(dist);            // pixel-space edge width
          return 1.0 - smoothstep(0.0, w, dist);
        }

        void main(){
          vec2 pos    = gl_FragCoord.xy;

          // Scale spacing by DPR to maintain consistent dot size across different pixel ratios
          float spacingScaled = u_spacing * u_dpr;
          vec2 cell   = floor(pos / spacingScaled);
          vec2 center = (cell + 0.5) * spacingScaled;



          // 2) slow global noise per cell
          vec2 nUV = cell * u_noiseScale + vec2(0.0, u_time * u_noiseSpeed);
          float noiseActiv = valueNoise(nUV) * u_noiseAmp;

          // 3) mouse cloud with noisy boundary (as you already have)
          vec2 mUV = cell * u_mouseNoiseScale + vec2(u_time * u_mouseNoiseSpeed, 0.0);
          float mN = valueNoise(mUV) * 2.0 - 1.0;
          float baseR = (u_mouseRadiusBase + u_mouseRadiusBoost) * u_dpr;
          float rEff  = baseR * (1.0 + mN * u_mouseNoiseAmp);
          float fEff  = u_mouseFalloff * u_dpr * (1.0 + mN * u_mouseNoiseAmp * 0.5);
          float cd    = length(center - u_mouse); // distance from dot center to mouse
          float m     = 1.0 - smoothstep(rEff, rEff + fEff, cd);
          float mouseActiv = m * u_mouseAmp;

           // 1) geometry: crisp dots (AA only at edge)
          float progress = 1.0 - smoothstep((u_fillWaveR - u_fillWaveW) * u_dpr,
                                            (u_fillWaveR + u_fillWaveW) * u_dpr,
                                            cd);
          float fillLocal = mix(u_fillFrom, u_fillTo, clamp(progress, 0.0, 1.0));
          float radiusPx  = spacingScaled * fillLocal;
          float inDot = circleAA(pos - center, radiusPx);

          // 4) combine → activation
          float activ = clamp(noiseActiv + mouseActiv, 0.0, 1.0);

          // 5) dot state (filled vs hole)
          float filled = step(u_threshold, activ);
          if (u_invert > 0.5) filled = 1.0 - filled;

          // 6) alpha: outside dot => 1; inside dot => filled?1:0
          float alphaInside = filled;
          float alpha = mix(1.0, alphaInside, inDot);

          gl_FragColor = vec4(u_bg, alpha);    // u_bg is LINEAR (TS converts)
          #include <colorspace_fragment>       // convert to renderer.outputColorSpace (sRGB)
        }
      `,
      uniforms: this.uniforms,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    });

    this.mesh = new THREE.Mesh(geometry, material);
    this.scene.add(this.mesh);

    // Initialize GUI and FPS counter if enabled
    if (this.debug()) {
      this.makeGUI();
    }

    if (this.unfoldGui()) {
      this.makeFPS();
    }

    // Start the optimized render loop
    this.updateSize();

    // Low-performance mode: Render only once (static frame)
    // Use requestAnimationFrame to ensure WebGL context is ready
    if (this.lowPerformance()) {
      requestAnimationFrame(timestamp => {
        this.renderSingleFrame(timestamp);
      });
      return;
    }

    // Normal/Safari mode: Continuous render loop
    const renderLoop = (timestamp: number) => {
      // Always request next frame
      this.raf = requestAnimationFrame(renderLoop);

      // Skip rendering when component is not visible in viewport for performance
      if (!this.isInViewport()) {
        return;
      }

      // Firefox/Safari: Use time-based throttling for better performance
      const lowPerformance = this.platform.SAFARI || this.platform.FIREFOX;
      // Safari: 33ms = 30fps. Everything else (incl. Firefox and mobile, which render at a reduced resolution) 60fps
      const throttleMs = this.isMobile() || this.platform.FIREFOX ? this.RENDER_THROTTLE_MS : lowPerformance ? 33 : this.RENDER_THROTTLE_MS;

      if (timestamp - this.lastRenderTime < throttleMs) {
        return;
      }

      this.lastRenderTime = timestamp;

      // Use cached values instead of reading signals every frame
      const sizeData = this.cachedSizeData;
      const devicePixelRatio = this.cachedDevicePixelRatio;

      // Always use 1.0 for DPR in shader calculations to maintain consistent visual dot size
      // The renderer's setPixelRatio handles the actual resolution scaling
      this.uniforms['u_dpr'].value = this.dprScale(sizeData.dpr);

      // Update time uniform (always changes)
      this.uniforms['u_time'].value = timestamp * 0.001;

      // Update mouse position (check if changed to avoid unnecessary vector operations)
      const smoothMousePosition = this.smoothMouse();
      const mouseX = smoothMousePosition.x * devicePixelRatio;
      const mouseY = (sizeData.h - smoothMousePosition.y) * devicePixelRatio;
      const currentMouse = this.uniforms['u_mouse'].value;
      if (currentMouse.x !== mouseX || currentMouse.y !== mouseY) {
        currentMouse.set(mouseX, mouseY);
      }

      // Update resolution only if changed
      const resUniform = this.uniforms['u_res'].value;
      const targetW = sizeData.w * devicePixelRatio;
      const targetH = sizeData.h * devicePixelRatio;
      if (resUniform.x !== targetW || resUniform.y !== targetH) {
        resUniform.set(targetW, targetH);
      }

      // Batch uniform updates: collect changes first, then apply
      const uniformUpdates: Array<() => void> = [];

      // Only update uniforms that have changed (reduces CPU overhead)
      if (this.lastParams.background !== this.params.background) {
        uniformUpdates.push(() => {
          (this.uniforms['u_bg'].value as THREE.Color).set(this.params.background).convertSRGBToLinear();
        });
        this.lastParams.background = this.params.background;
      }

      if (this.lastParams.spacing !== this.params.spacing) {
        uniformUpdates.push(() => {
          this.uniforms['u_spacing'].value = this.params.spacing;
        });
        this.lastParams.spacing = this.params.spacing;
      }

      if (this.lastParams.fill !== this.params.fill || this.lastParams.spacing !== this.params.spacing) {
        uniformUpdates.push(() => {
          this.uniforms['u_radius'].value = this.params.spacing * this.params.fill;
        });
        this.lastParams.fill = this.params.fill;
      }

      // Group related uniforms and only update if changed
      if (this.lastParams.noiseScale !== this.params.noiseScale) {
        uniformUpdates.push(() => {
          this.uniforms['u_noiseScale'].value = this.params.noiseScale;
        });
        this.lastParams.noiseScale = this.params.noiseScale;
      }
      if (this.lastParams.noiseAmp !== this.params.noiseAmp) {
        uniformUpdates.push(() => {
          this.uniforms['u_noiseAmp'].value = this.params.noiseAmp;
        });
        this.lastParams.noiseAmp = this.params.noiseAmp;
      }
      if (this.lastParams.noiseSpeed !== this.params.noiseSpeed) {
        uniformUpdates.push(() => {
          this.uniforms['u_noiseSpeed'].value = this.params.noiseSpeed;
        });
        this.lastParams.noiseSpeed = this.params.noiseSpeed;
      }
      if (this.lastParams.threshold !== this.params.threshold) {
        uniformUpdates.push(() => {
          this.uniforms['u_threshold'].value = this.params.threshold;
        });
        this.lastParams.threshold = this.params.threshold;
      }

      // Mouse uniforms (frequently changing, but still check)
      if (this.lastParams.mouseRadius !== this.params.mouseRadius) {
        uniformUpdates.push(() => {
          this.uniforms['u_mouseRadiusBase'].value = this.params.mouseRadius;
        });
        this.lastParams.mouseRadius = this.params.mouseRadius;
      }
      if (this.lastParams.radiusBoost !== this.params.radiusBoost) {
        uniformUpdates.push(() => {
          this.uniforms['u_mouseRadiusBoost'].value = this.params.radiusBoost;
        });
        this.lastParams.radiusBoost = this.params.radiusBoost;
      }
      if (this.lastParams.mouseFalloff !== this.params.mouseFalloff) {
        uniformUpdates.push(() => {
          this.uniforms['u_mouseFalloff'].value = this.params.mouseFalloff;
        });
        this.lastParams.mouseFalloff = this.params.mouseFalloff;
      }
      if (this.lastParams.mouseAmp !== this.params.mouseAmp) {
        uniformUpdates.push(() => {
          this.uniforms['u_mouseAmp'].value = this.params.mouseAmp;
        });
        this.lastParams.mouseAmp = this.params.mouseAmp;
      }

      // Mouse noise uniforms
      if (this.lastParams.mouseNoiseScale !== this.params.mouseNoiseScale) {
        uniformUpdates.push(() => {
          this.uniforms['u_mouseNoiseScale'].value = this.params.mouseNoiseScale;
        });
        this.lastParams.mouseNoiseScale = this.params.mouseNoiseScale;
      }
      if (this.lastParams.mouseNoiseAmp !== this.params.mouseNoiseAmp) {
        uniformUpdates.push(() => {
          this.uniforms['u_mouseNoiseAmp'].value = this.params.mouseNoiseAmp;
        });
        this.lastParams.mouseNoiseAmp = this.params.mouseNoiseAmp;
      }
      if (this.lastParams.mouseNoiseSpeed !== this.params.mouseNoiseSpeed) {
        uniformUpdates.push(() => {
          this.uniforms['u_mouseNoiseSpeed'].value = this.params.mouseNoiseSpeed;
        });
        this.lastParams.mouseNoiseSpeed = this.params.mouseNoiseSpeed;
      }

      // Fill and wave animation uniforms
      if (this.lastParams.fillRevealTarget !== this.params.fillRevealTarget) {
        uniformUpdates.push(() => {
          this.uniforms['u_fillFrom'].value = this.params.fill;
          this.uniforms['u_fillTo'].value = this.params.fillRevealTarget;
        });
        this.lastParams.fillRevealTarget = this.params.fillRevealTarget;
      }
      if (this.lastParams.fillWaveR !== this.params.fillWaveR) {
        uniformUpdates.push(() => {
          this.uniforms['u_fillWaveR'].value = this.params.fillWaveR;
        });
        this.lastParams.fillWaveR = this.params.fillWaveR;
      }
      if (this.lastParams.fillWaveWidth !== this.params.fillWaveWidth) {
        uniformUpdates.push(() => {
          this.uniforms['u_fillWaveW'].value = this.params.fillWaveWidth;
        });
        this.lastParams.fillWaveWidth = this.params.fillWaveWidth;
      }

      if (this.lastParams.invert !== this.params.invert) {
        uniformUpdates.push(() => {
          this.uniforms['u_invert'].value = this.params.invert ? 1.0 : 0.0;
        });
        this.lastParams.invert = this.params.invert;
      }

      // Apply all uniform updates in batch
      uniformUpdates.forEach(update => update());

      // Render the scene
      this.renderer.render(this.scene, this.camera);

      // Emit initialization signal once on first successful render
      if (!this.emittedInited()) {
        this.ngZone.runOutsideAngular(() => {
          requestAnimationFrame(() => {
            this.inited.emit(true);
          });
        });
        this.emittedInited.set(true);
      }

      // Update FPS counter for debugging
      this.tickFPS();
    };

    // Start the render loop
    requestAnimationFrame(renderLoop);
  }

  /**
   * Render a single static frame for low-performance mode
   * No animation loop, just one render with initial state
   */
  private renderSingleFrame(timestamp: number): void {
    if (!this.renderer || !this.scene || !this.camera || !this.uniforms) {
      return;
    }

    const sizeData = this.size();

    // Safari-specific: limit pixel ratio to reduce GPU memory usage
    const devicePixelRatio = this.renderDpr(sizeData.dpr);

    // Always use 1.0 for DPR in shader to maintain consistent visual dot size
    this.uniforms['u_dpr'].value = this.dprScale(sizeData.dpr);

    // Set initial time uniform (static)
    this.uniforms['u_time'].value = timestamp * 0.001;

    // Set initial mouse position
    const smoothMousePosition = this.smoothMouse();
    const mouseX = smoothMousePosition.x * devicePixelRatio;
    const mouseY = (sizeData.h - smoothMousePosition.y) * devicePixelRatio;
    this.uniforms['u_mouse'].value.set(mouseX, mouseY);

    // Set resolution
    const targetW = sizeData.w * devicePixelRatio;
    const targetH = sizeData.h * devicePixelRatio;
    this.uniforms['u_res'].value.set(targetW, targetH);

    // Set all initial uniform values (no need to check for changes since it's only once)
    (this.uniforms['u_bg'].value as THREE.Color).set(this.params.background).convertSRGBToLinear();
    this.uniforms['u_spacing'].value = this.params.spacing;
    this.uniforms['u_radius'].value = this.params.spacing * this.params.fill;

    this.uniforms['u_noiseScale'].value = this.params.noiseScale;
    this.uniforms['u_noiseAmp'].value = this.params.noiseAmp;
    this.uniforms['u_noiseSpeed'].value = this.params.noiseSpeed;
    this.uniforms['u_threshold'].value = this.params.threshold;

    this.uniforms['u_mouseRadiusBase'].value = this.params.mouseRadius;
    this.uniforms['u_mouseRadiusBoost'].value = this.params.radiusBoost;
    this.uniforms['u_mouseFalloff'].value = this.params.mouseFalloff;
    this.uniforms['u_mouseAmp'].value = this.params.mouseAmp;

    this.uniforms['u_mouseNoiseScale'].value = this.params.mouseNoiseScale;
    this.uniforms['u_mouseNoiseAmp'].value = this.params.mouseNoiseAmp;
    this.uniforms['u_mouseNoiseSpeed'].value = this.params.mouseNoiseSpeed;

    this.uniforms['u_fillFrom'].value = this.params.fill;
    this.uniforms['u_fillTo'].value = this.params.fillRevealTarget;
    this.uniforms['u_fillWaveR'].value = this.params.fillWaveR;
    this.uniforms['u_fillWaveW'].value = this.params.fillWaveWidth;

    this.uniforms['u_invert'].value = this.params.invert ? 1.0 : 0.0;

    // Render the scene once
    this.renderer.render(this.scene, this.camera);

    // Emit initialization signal
    if (!this.emittedInited()) {
      this.ngZone.runOutsideAngular(() => {
        requestAnimationFrame(() => {
          this.inited.emit(true);
        });
      });
      this.emittedInited.set(true);
    }
  }

  /**
   * Create debugging GUI for real-time parameter adjustment
   */
  private makeGUI() {
    this.gui?.destroy();
    this.gui = new GUI({ title: 'Dot Mask Controls', width: 320 });

    const fGrid = this.gui.addFolder('Grid');
    fGrid.add(this.params, 'spacing', 12, 64, 1);
    fGrid.add(this.params, 'fill', 0.1, 1, 0.01);
    fGrid.addColor(this.params, 'background');

    fGrid.add(this.params, 'fillRevealTarget', 0.1, 1.0, 0.01).name('reveal fill');
    fGrid.add(this.params, 'fillWaveWidth', 10, 300, 1).name('reveal band width px');
    fGrid.add(this.params, 'fillWaveR', 0, 2000, 1).name('reveal wave radius px');

    const fNoise = this.gui.addFolder('Global Noise');
    fNoise.add(this.params, 'noiseScale', 0.1, 1.2, 0.01).name('scale');
    fNoise.add(this.params, 'noiseAmp', 0.0, 1.0, 0.01).name('amplitude');
    fNoise.add(this.params, 'noiseSpeed', 0.0, 1.0, 0.01).name('speed');
    fNoise.add(this.params, 'threshold', 0.0, 1.0, 0.01);

    const fMouse = this.gui.addFolder('Mouse Influence');
    fMouse.add(this.params, 'mouseRadius', 0, 600, 1).name('base radius');
    fMouse.add(this.params, 'mouseFalloff', 0, 300, 1).name('falloff');
    fMouse.add(this.params, 'mouseAmp', 0.0, 2.0, 0.01).name('intensity');

    const fMouseNoise = this.gui.addFolder('Mouse Cloud (Noise)');
    fMouseNoise.add(this.params, 'mouseNoiseScale', 0.1, 1.5, 0.01).name('scale');
    fMouseNoise.add(this.params, 'mouseNoiseAmp', 0.0, 1.0, 0.01).name('amplitude');
    fMouseNoise.add(this.params, 'mouseNoiseSpeed', 0.0, 2.0, 0.01).name('speed');

    const fVelocity = this.gui.addFolder('Velocity Boost');
    fVelocity.add(this.params, 'velocityScale', 0.0, 0.6, 0.01).name('px/ (px·s⁻¹)');
    fVelocity.add(this.params, 'velocityMaxBoost', 0, 400, 1).name('max boost px');
    fVelocity.add(this.params, 'boostResponse', 0.05, 0.6, 0.01).name('response s');
    fVelocity.add(this.params, 'leaveShrinkDuration', 0.05, 1.0, 0.01).name('leave shrink s');

    const fFollow = this.gui.addFolder('Follow (GSAP)');
    fFollow.add(this.params, 'followDuration', 0.0, 1.2, 0.01);
    fFollow.add(this.params, 'followEase', [
      'power1.out',
      'power2.out',
      'power3.out',
      'power4.out',
      'expo.out',
      'sine.out',
      'back.out(1.7)',
    ]);

    const fLogic = this.gui.addFolder('Logic');

    fLogic.add(this.params, 'invert').name('invert fill/holes');

    const fGyro = this.gui.addFolder('Gyro (Tilt Control)');
    fGyro
      .add(this.params, 'gyroEnabled')
      .name('enabled')
      .onChange(() => {
        if (this.params.gyroEnabled) this.enableGyroIfTouch();
        else {
          window.removeEventListener('deviceorientation', this.onDeviceOrientation as any);
          this.gyroActive = false;
        }
      });
    fGyro.add(this.params, 'gyroStrength', 0.2, 2.5, 0.05);
    fGyro.add(this.params, 'gyroDeadzoneDeg', 0, 10, 0.5);
    fGyro.add(this.params, 'gyroMaxTiltDeg', 5, 60, 1);
    fGyro.add(this.params, 'gyroEaseDuration', 0.05, 2.0, 0.01);
    fGyro
      .add(
        {
          calibrate: () => {
            this.neutral = { beta: 0, gamma: 0 };
          },
        },
        'calibrate'
      )
      .name('Reset neutral');
    fGyro
      .add(
        {
          setNeutral: () => {
            this.neutral = { ...this.lastRawTilt };
          },
        },
        'setNeutral'
      )
      .name('Set neutral (current)');

    fGrid.close();
    fGyro.close();
    fNoise.close();
    fMouse.close();
    fMouseNoise.close();
    fVelocity.close();
    fFollow.close();
    fLogic.close();

    const presets = this.gui.addFolder('Presets');

    // switch between otherProposals
    presets
      .add(
        {
          preset: paramProposals[0].name,
        },
        'preset',
        paramProposals.map(p => p.name)
      )
      .name('Try a preset!')
      .onChange((v: string) => {
        const p = paramProposals.find(pp => pp.name === v);
        if (p) {
          // apply params
          this.params = { ...this.params, ...p.params };
        }
      });

    const presetSettings = {
      name: 'Voorstel #' + paramProposals.length,
    };
    presets.add(presetSettings, 'name').name('Name your new preset');
    this.gui
      .add(
        {
          newPreset: () => {
            const p = { ...this.params };
            delete (p as any)._gsap;

            const proposal = {
              name: presetSettings.name || 'Voorstel #' + (paramProposals.length + 1),
              params: p,
            };

            // TODO add const with name to clipboard?

            console.log('See addfavoriteparams.ts and add your new preset to the list: paramProposals.');
            console.log('The object is already copied to your clipboard:');
            console.log(proposal);
            // copy to clipboard
            navigator.clipboard.writeText(JSON.stringify(proposal, null, 2));
          },
        },
        'newPreset'
      )
      .name('Copy to clipboard (see console)');

    if (!this.unfoldGui()) {
      this.gui.close();
    }
  }

  /**
   * Create FPS display element for performance monitoring
   */
  private makeFPS() {
    if (!isPlatformBrowser(this.platformId)) return;
    const hostElement = this.canvasRef.nativeElement.parentElement as HTMLElement;
    const fpsElement = document.createElement('div');
    fpsElement.className = 'dm-fps';

    // Show static indicator for low-performance mode
    if (this.lowPerformance()) {
      fpsElement.textContent = 'STATIC • LOW-PERF (single frame)';
    } else {
      fpsElement.textContent = 'FPS: --';
    }

    hostElement.appendChild(fpsElement);
    this.fpsEl = fpsElement;
  }

  /**
   * Update FPS counter every 500ms for performance monitoring
   */
  private tickFPS() {
    // Don't update FPS counter in low-performance mode (static frame)
    if (this.lowPerformance()) return;
    const lowPerformance = this.platform.SAFARI || this.platform.FIREFOX;

    this.frames++;
    const currentTime = performance.now();
    if (currentTime - this.lastTime >= 500) {
      this.fps = Math.round((this.frames * 1000) / (currentTime - this.lastTime));
      this.frames = 0;
      this.lastTime = currentTime;
      if (this.fpsEl) {
        // Show performance mode in FPS display
        let mode = '';
        if (lowPerformance) {
          if (this.platform.FIREFOX) {
            mode = ' • Firefox (1x resolution)';
          } else {
            mode = ' • Safari (30fps throttle)';
          }
        }

        this.fpsEl.textContent = `FPS ${this.fps}${mode} • Boost ${Math.round(this.params.radiusBoost)}px • Noise ${this.params.noiseAmp.toFixed(2)}`;
      }
    }
  }

  /**
   * Handle window resize events with throttling for better performance
   */
  @HostListener('window:resize')
  onResize() {
    this.updateSize();
  }

  /**
   * Update component size based on parent container dimensions
   */
  private updateSize() {
    const hostElement = this.canvasRef.nativeElement.parentElement!;
    const boundingRect = hostElement.getBoundingClientRect();
    const newSize = {
      w: boundingRect.width,
      h: boundingRect.height,
      dpr: window.devicePixelRatio || 1,
    };

    // Update signal which triggers the effect
    this.size.set(newSize);

    // Also update cached values immediately for render loop
    this.cachedSizeData = { ...newSize };

    // Recalculate cached pixel ratio
    this.cachedDevicePixelRatio = this.renderDpr(newSize.dpr);
  }

  /**
   * Check if the current device supports touch input
   */
  private isTouchDevice(): boolean {
    return ((navigator.maxTouchPoints ?? 0) > 0 || matchMedia('(pointer: coarse)').matches) && (this.platform.ANDROID || this.platform.IOS);
  }

  /**
   * Set default mouse position when component initializes
   */
  private setDefaultMouseIfUninitialized() {
    // Use cached size data instead of signal read
    const sizeData = this.cachedSizeData.w > 0 ? this.cachedSizeData : this.size();
    const defaultPosition = { x: sizeData.w * 0.75, y: sizeData.h * 0.5 }; // Right-center position

    this.targetMouse.set(defaultPosition);
    this.smoothMouse.set(defaultPosition);

    // check if screen width is smaller than 750px, then set to center
    if (sizeData.w < 750) {
      defaultPosition.x = sizeData.w * 0.5;
      defaultPosition.y = sizeData.h * 0.75;
    }

    if (this.targetMouse().x === -9999 && this.targetMouse().y === -9999) {
      this.targetMouse.set(defaultPosition);
      this.smoothMouse.set(defaultPosition);
    }

    // Initialize velocity tracking to prevent boost calculation spikes
    this.prevTarget = { x: defaultPosition.x, y: defaultPosition.y, t: performance.now() };
    this.lastGyro = { x: defaultPosition.x, y: defaultPosition.y, t: performance.now() };
  }

  /**
   * Enable gyroscope control for touch devices if supported and enabled
   */
  private async enableGyroIfTouch() {
    if (!this.isTouchDevice() || !this.params.gyroEnabled) return;

    // Disable velocity boost when using gyroscope control
    this.params = { ...this.params, velocityScale: 0 };

    const requestDeviceOrientation = (window as any).DeviceOrientationEvent?.requestPermission;
    const requestDeviceMotion = (window as any).DeviceMotionEvent?.requestPermission;

    const startGyroListening = () => {
      window.addEventListener('deviceorientation', this.onDeviceOrientation as EventListener, { passive: true });
      this.gyroActive = true;
    };

    // For Android and most browsers that don't require permission
    if (typeof requestDeviceOrientation !== 'function' && typeof requestDeviceMotion !== 'function') {
      startGyroListening();
      return;
    }

    // For iOS devices that require permission on first user interaction
    const canvas = this.canvasRef.nativeElement;
    const requestPermissionOnce = async () => {
      try {
        const permissionResult = requestDeviceOrientation
          ? await requestDeviceOrientation.call((window as any).DeviceOrientationEvent)
          : await requestDeviceMotion!.call((window as any).DeviceMotionEvent);

        if (permissionResult === 'granted') {
          startGyroListening();
        }
      } catch (error) {
        console.warn('Failed to request device orientation permission:', error);
      }

      // Remove listeners after first attempt regardless of outcome
      canvas.removeEventListener('pointerdown', requestPermissionOnce);
      canvas.removeEventListener('touchend', requestPermissionOnce);
      canvas.removeEventListener('click', requestPermissionOnce);
    };

    // Listen for user interaction to request permissions
    canvas.addEventListener('pointerdown', requestPermissionOnce, { once: true });
    canvas.addEventListener('touchend', requestPermissionOnce, { once: true });
    canvas.addEventListener('click', requestPermissionOnce, { once: true });
  }

  private onDeviceOrientation = (e: DeviceOrientationEvent) => {
    if (e.beta == null || e.gamma == null) return;

    // Raw tilt in degrees
    let beta = e.beta; // front-back: -180..180
    let gamma = e.gamma; // left-right:  -90..90

    // --- NEW: auto-calibrate once ---
    if (!this.hasNeutral) {
      this.neutral = { beta, gamma }; // take current hold as “neutral”
      this.hasNeutral = true;
    }

    // Keep for “set neutral” GUI etc.
    this.lastRawTilt.beta = beta ?? 0;
    this.lastRawTilt.gamma = gamma ?? 0;

    // Subtract neutral
    beta -= this.neutral.beta;
    gamma -= this.neutral.gamma;

    // ── axis mapping (portrait) ──
    let tiltX = gamma;
    let tiltY = +beta; // forward tilt moves forward (reversed from earlier)

    // Rotate mapping in landscape so "downhill" still feels right
    const angle =
      screen.orientation && 'angle' in screen.orientation
        ? ((screen.orientation as any).angle as number)
        : (window.orientation as number) || 0;

    if (Math.abs(angle) === 90) {
      const sign = angle === 90 ? 1 : -1;
      const x = tiltX,
        y = tiltY;
      tiltX = sign * y;
      tiltY = -sign * x;
    }

    // Deadzone + clamp
    const dz = this.params.gyroDeadzoneDeg;
    const maxDeg = this.params.gyroMaxTiltDeg;
    const dzApply = (v: number) => Math.sign(v) * Math.max(0, Math.abs(v) - dz);
    tiltX = THREE.MathUtils.clamp(dzApply(tiltX), -maxDeg, maxDeg);
    tiltY = THREE.MathUtils.clamp(dzApply(tiltY), -maxDeg, maxDeg);

    // Normalize -1..1 → screen coords
    const nx = tiltX / maxDeg;
    const ny = tiltY / maxDeg;
    // Use cached size data instead of signal read
    const s = this.cachedSizeData;
    const strength = this.params.gyroStrength;
    const topOffsetRatio = 0.25; // 25% from top
    const topOffsetPercent = s.h * topOffsetRatio;
    const targetX = s.w * 0.5 + nx * (s.w * 0.5) * strength;
    const targetY = topOffsetPercent + s.h * 0.5 + ny * (s.h * 0.5) * strength;

    // GSAP smooth follow
    this.smoothTween?.kill();
    const sm = { ...this.smoothMouse() };
    this.smoothTween = gsap.to(sm, {
      x: targetX,
      y: targetY,
      duration: this.params.gyroEaseDuration,
      ease: this.params.followEase,
      onUpdate: () => this.smoothMouse.set({ x: sm.x, y: sm.y }),
    });

    // Velocity-based radius boost
    const now = performance.now();
    const dt = Math.max(1, now - this.lastGyro.t);
    const dx = targetX - this.lastGyro.x;
    const dy = targetY - this.lastGyro.y;
    const speed = (Math.hypot(dx, dy) * 1000) / dt;
    this.lastGyro = { x: targetX, y: targetY, t: now };
    const boostTarget = Math.min(speed * this.params.velocityScale, this.params.velocityMaxBoost);
    gsap.to(this.params, { radiusBoost: boostTarget, duration: this.params.boostResponse, ease: 'power2.out' });
  };

  public startReveal() {
    // Disable reveal animations in low-performance mode
    if (this.lowPerformance()) return;

    // KILL oude TL
    //this.revealTl?.kill();
    //this.revealTl = undefined;
    if (this.revealTl) return; // al bezig

    // Zorg dat we een nette startpositie hebben
    this.setDefaultMouseIfUninitialized();

    // Bewaar de huidige waarden (éénmalig per start)
    this.revealDefaults = {
      mouseRadius: this.params.mouseRadius,
      mouseNoiseAmp: this.params.mouseNoiseAmp,
      fill: this.params.fill,
      fillRevealTarget: this.params.fillRevealTarget,
      fillWaveWidth: this.params.fillWaveWidth,
      fillWaveR: this.params.fillWaveR,
    };

    // Optioneel: zeker weten dat ‘reveal’ echt “beeld tonen” betekent.
    // Met jouw shader/logica is ‘invert: true’ de variant waarbij hoge activatie tot holes leidt.
    // Alleen als je zeker weet dat jij al invert=true gebruikt, kun je deze skippen.
    if (!this.params.invert) this.params.invert = true;

    // Use cached size data instead of signal read
    const s = this.cachedSizeData.w > 0 ? this.cachedSizeData : this.size();
    const diag = Math.hypot(s.w, s.h) * 2; // diagonaal → max reikwijdte
    const duration = 1.0; // seconds

    this.revealTl = gsap
      .timeline({ defaults: { ease: 'power3.out' } })
      // 1) Expandeer de muisradius zodat de wolk uitzet over (bijna) het hele scherm
      .to(
        this.params,
        {
          mouseRadius: diag, // groot genoeg om alles te pakken
          mouseNoiseAmp: 0, // weg met ruis tijdens reveal
          threshold: 0.5,
          duration: duration,
          ease: 'power1.inOut',
        },
        0
      )

      .to(
        this.params,
        {
          fillRevealTarget: 1.0,
          fillWaveWidth: diag,
          fillWaveR: diag * 0.75,
          duration: duration * 1.2,
          ease: 'power1.inOut',
        },
        `-=${duration}` // overlap
      );
  }

  public resetReveal() {
    // Disable reveal animations in low-performance mode
    //if (this.lowPerformance()) return;

    // Geen defaults → niets te resetten
    if (!this.revealDefaults) return;

    this.revealTl?.kill();
    this.revealTl = undefined;

    this.revealTl = gsap
      .timeline({ defaults: { ease: 'power3.out' } })
      .to(
        this.params,
        {
          mouseRadius: this.revealDefaults.mouseRadius,
          mouseNoiseAmp: this.revealDefaults.mouseNoiseAmp,
          fill: this.revealDefaults.fill,
          duration: 0.6,
          ease: 'power2.inOut',
          onComplete: () => {
            this.revealTl = undefined;
          },
        },
        0
      )
      .to(
        this.params,
        {
          fillRevealTarget: this.revealDefaults.fillRevealTarget,
          fillWaveWidth: this.revealDefaults.fillWaveWidth,
          fillWaveR: this.revealDefaults.fillWaveR,
          duration: 1,
          ease: 'power2.out',
        },
        '-=0.8'
      )
      // Boost terug naar 0, zodat de wolk niet blijft ‘pompen’
      .to(
        this.params,
        {
          radiusBoost: 0,
          duration: 0.4,
          ease: 'power1.out',
        },
        0
      );

    this.revealDefaults = undefined;
  }
}
